import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Star,
  EyeOff,
  Eye,
  Camera,
  Aperture,
  Timer,
  Zap,
  Maximize2,
  Users,
  Download,
  Clock,
} from 'lucide-react';

import { usePhoto, useUpdatePhoto } from '@/api/hooks/usePhotos';
import apiClient from '@/api/client';
import {
  getPhotoFileUrl,
  getPhotoThumbnailUrl,
} from '@/api/services/photo.service';
import { QualityScoreMeter } from './QualityScoreMeter';
import { BestPickBadge } from './BestPickBadge';

interface PhotoDetailPanelProps {
  photoId: string;
  onClose: () => void;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
}

/**
 * Slide-in panel showing full photo detail with EXIF, quality scores,
 * face detections, captions, and controls.
 */
export const PhotoDetailPanel = React.memo(function PhotoDetailPanel({
  photoId,
  onClose,
  onPrev,
  onNext,
}: PhotoDetailPanelProps) {
  const { t } = useTranslation();
  const { data: photo, isLoading, isError } = usePhoto(photoId);
  const updateMutation = useUpdatePhoto();

  const handleToggleStar = useCallback(() => {
    if (!photo) return;
    updateMutation.mutate({
      photoId: photo.id,
      payload: { is_starred: !photo.is_starred },
    });
  }, [photo, updateMutation]);

  const handleToggleHide = useCallback(() => {
    if (!photo) return;
    updateMutation.mutate({
      photoId: photo.id,
      payload: { is_hidden: !photo.is_hidden },
    });
  }, [photo, updateMutation]);

  // Keyboard navigation
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col overflow-y-auto bg-white shadow-xl dark:bg-gray-900 sm:max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {onPrev && (
              <button
                onClick={onPrev}
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                title={t('actions.previous')}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            {onNext && (
              <button
                onClick={onNext}
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                title={t('actions.next')}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {photo && (
              <>
                <button
                  onClick={handleToggleStar}
                  className={`rounded-lg p-1.5 transition-colors ${
                    photo.is_starred
                      ? 'text-yellow-500'
                      : 'text-gray-400 hover:text-yellow-500'
                  }`}
                  title={photo.is_starred ? t('gallery.unstar') : t('gallery.star')}
                >
                  <Star
                    className="h-5 w-5"
                    fill={photo.is_starred ? 'currentColor' : 'none'}
                  />
                </button>
                <button
                  onClick={handleToggleHide}
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                  title={photo.is_hidden ? t('gallery.unhide') : t('gallery.hide')}
                >
                  {photo.is_hidden ? (
                    <Eye className="h-5 w-5" />
                  ) : (
                    <EyeOff className="h-5 w-5" />
                  )}
                </button>
                <a
                  href={getPhotoFileUrl(photo.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                  title={t('actions.download')}
                >
                  <Download className="h-5 w-5" />
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            </div>
          )}

          {isError && (
            <div className="p-6 text-center text-sm text-red-500">
              {t('status.error')}
            </div>
          )}

          {photo && (
            <>
              {/* Image preview */}
              <div className="bg-gray-100 dark:bg-gray-800">
                <img
                  src={
                    photo.thumbnail_large
                      ? getPhotoThumbnailUrl(photo.id, 'large')
                      : getPhotoThumbnailUrl(photo.id, 'medium')
                  }
                  alt={photo.original_filename}
                  className="mx-auto max-h-[400px] w-auto object-contain"
                />
              </div>

              <div className="space-y-5 p-4">
                {/* Filename & badges */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {photo.original_filename}
                  </h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {photo.is_group_best && <BestPickBadge />}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {photo.pipeline_status}
                    </span>
                    {photo.phase_label && (
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                        {photo.phase_label}
                      </span>
                    )}
                  </div>
                </div>

                {/* Relative time from event start */}
                {photo.taken_at && (
                  <Section title={t('gallery.relativeTime')}>
                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>
                        {(() => {
                          const takenAt = new Date(photo.taken_at!);
                          const eventStart = photo.event_start_time ? new Date(photo.event_start_time) : null;
                          if (!eventStart) return new Date(photo.taken_at!).toLocaleString();
                          const diffMs = takenAt.getTime() - eventStart.getTime();
                          const diffMins = Math.floor(diffMs / 60000);
                          const hours = Math.floor(diffMins / 60);
                          const mins = diffMins % 60;
                          if (hours > 0) return t('gallery.relativeHoursMinutes', { hours, minutes: mins });
                          return t('gallery.relativeMinutes', { minutes: mins });
                        })()}
                      </span>
                    </div>
                  </Section>
                )}

                {/* Quality scores */}
                <Section title={t('gallery.quality')}>
                  <div className="space-y-2">
                    <ScoreRow
                      label={t('gallery.overall')}
                      score={photo.quality_score}
                    />
                    <ScoreRow
                      label={t('gallery.sharpness')}
                      score={photo.sharpness_score}
                    />
                    <ScoreRow
                      label={t('gallery.exposure')}
                      score={photo.exposure_score}
                    />
                    <ScoreRow
                      label={t('gallery.blur')}
                      score={photo.blur_score}
                    />
                    <ScoreRow
                      label={t('gallery.noise')}
                      score={photo.noise_score}
                    />
                    <div className="flex gap-3 pt-1">
                      {photo.is_blurry && (
                        <FlagPill label={t('gallery.blurry')} color="red" />
                      )}
                      {photo.is_overexposed && (
                        <FlagPill label={t('gallery.overexposed')} color="yellow" />
                      )}
                      {photo.is_underexposed && (
                        <FlagPill label={t('gallery.underexposed')} color="blue" />
                      )}
                    </div>
                  </div>
                </Section>

                {/* EXIF data */}
                <Section title={t('gallery.exif')}>
                  <div className="grid grid-cols-2 gap-2">
                    {photo.camera_make && (
                      <ExifItem
                        icon={<Camera className="h-3.5 w-3.5" />}
                        label={t('gallery.camera')}
                        value={`${photo.camera_make} ${photo.camera_model_name}`.trim()}
                      />
                    )}
                    {photo.lens_model && (
                      <ExifItem
                        icon={<Maximize2 className="h-3.5 w-3.5" />}
                        label={t('gallery.lens')}
                        value={photo.lens_model}
                      />
                    )}
                    {photo.focal_length_mm != null && (
                      <ExifItem
                        icon={<Maximize2 className="h-3.5 w-3.5" />}
                        label={t('gallery.focalLength')}
                        value={`${photo.focal_length_mm}mm`}
                      />
                    )}
                    {photo.aperture != null && (
                      <ExifItem
                        icon={<Aperture className="h-3.5 w-3.5" />}
                        label={t('gallery.aperture')}
                        value={`f/${photo.aperture}`}
                      />
                    )}
                    {photo.shutter_speed && (
                      <ExifItem
                        icon={<Timer className="h-3.5 w-3.5" />}
                        label={t('gallery.shutter')}
                        value={photo.shutter_speed}
                      />
                    )}
                    {photo.iso != null && (
                      <ExifItem
                        icon={<Zap className="h-3.5 w-3.5" />}
                        label="ISO"
                        value={String(photo.iso)}
                      />
                    )}
                    {photo.width != null && photo.height != null && (
                      <ExifItem
                        icon={<Maximize2 className="h-3.5 w-3.5" />}
                        label={t('gallery.dimensions')}
                        value={`${photo.width} x ${photo.height}`}
                      />
                    )}
                    {photo.taken_at && (
                      <ExifItem
                        icon={<Timer className="h-3.5 w-3.5" />}
                        label={t('gallery.takenAt')}
                        value={new Date(photo.taken_at).toLocaleString()}
                      />
                    )}
                  </div>
                </Section>

                {/* Face detections */}
                <Section title={t('gallery.faces')}>
                  {photo.face_count > 0 ? (
                    <FaceDetectionsPanel photoId={photo.id} faceCount={photo.face_count} t={t} />
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
                      <Users className="h-4 w-4" />
                      <span>{t('gallery.noFaces')}</span>
                    </div>
                  )}
                </Section>

                {/* Caption */}
                <Section title={t('gallery.caption')}>
                  {photo.caption_en ? (
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {photo.caption_en}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500">
                      {t('gallery.noCaption')}
                    </p>
                  )}
                  {photo.caption_he && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" dir="rtl">
                      {photo.caption_he}
                    </p>
                  )}
                </Section>

                {/* File info */}
                <Section title={t('gallery.fileInfo')}>
                  <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                    <p>
                      {t('gallery.size')}: {formatFileSize(photo.file_size)}
                    </p>
                    <p>
                      {t('gallery.type')}: {photo.content_type}
                    </p>
                    <p className="truncate">SHA256: {photo.checksum_sha256}</p>
                  </div>
                </Section>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
});

// ---------------------------------------------------------------------------
// Face Detections Panel
// ---------------------------------------------------------------------------

interface FaceData {
  id: string;
  bbox_x: number;
  bbox_y: number;
  bbox_width: number;
  bbox_height: number;
  crop_path: string;
  crop_url: string | null;
  confidence: number;
  face_diameter: number | null;
  prominence_score: number | null;
  distance_from_center: number | null;
  is_primary: boolean;
  person_name: string | null;
}

function FaceDetectionsPanel({
  photoId,
  faceCount,
  t,
}: {
  photoId: string;
  faceCount: number;
  t: ReturnType<typeof import('react-i18next').useTranslation>['t'];
}) {
  const [expanded, setExpanded] = useState(false);
  const [faces, setFaces] = useState<FaceData[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const handleToggle = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (faces.length > 0) return;

    setLoading(true);
    setFetchError(false);
    try {
      const { data } = await apiClient.get<{ faces: FaceData[] }>(
        '/faces/photo/' + photoId + '/'
      );
      setFaces(data.faces);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
      >
        <Users className="h-4 w-4" />
        <span className="underline underline-offset-2">
          {t('gallery.faceCount', { count: faceCount })}
        </span>
        <svg
          className={'h-3 w-3 transition-transform ' + (expanded ? 'rotate-180' : '')}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-3 space-y-2">
          {loading && (
            <div className="flex justify-center py-4">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
            </div>
          )}
          {fetchError && (
            <p className="text-xs text-red-500 dark:text-red-400">
              {t('status.error')}
            </p>
          )}
          {!loading && !fetchError && faces.length === 0 && faceCount > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {t('gallery.noFaces')}
            </p>
          )}
          {faces.map((face) => (
            <div
              key={face.id}
              className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 p-2 dark:border-surface-border dark:bg-surface-elevated"
            >
              {/* Face crop — prefer crop_path served via /media/ */}
              {face.crop_path && (
                <img
                  src={'/media/' + face.crop_path}
                  alt="face crop"
                  className="h-12 w-12 shrink-0 rounded-md object-cover bg-gray-200 dark:bg-gray-700"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                    {face.person_name || t('gallery.unknownPerson')}
                  </p>
                  {face.is_primary && (
                    <span className="rounded-full bg-primary-100 px-1.5 py-0.5 text-[9px] font-bold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                      {t('gallery.primaryFace')}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                  {face.face_diameter != null && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {t('gallery.faceDiameter', { px: face.face_diameter })}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">
                    {t('gallery.faceConfidence', { confidence: Math.round(face.confidence * 100) })}
                  </span>
                  {face.prominence_score != null && (
                    <span className={'text-[10px] font-medium ' + (
                      face.prominence_score > 0.6
                        ? 'text-green-600 dark:text-green-400'
                        : face.prominence_score > 0.3
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-gray-400 dark:text-gray-500'
                    )}>
                      {face.prominence_score > 0.6
                        ? t('gallery.faceProminent')
                        : face.prominence_score > 0.3
                        ? t('gallery.faceMedium')
                        : t('gallery.faceBackground')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {title}
      </h4>
      {children}
    </div>
  );
}

function ScoreRow({ label, score }: { label: string; score: number | null }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-20 shrink-0 text-xs text-gray-600 dark:text-gray-400">
        {label}
      </span>
      <QualityScoreMeter score={score} variant="bar" className="flex-1" />
    </div>
  );
}

function ExifItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5 text-gray-400">{icon}</span>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-400">
          {label}
        </p>
        <p className="truncate text-xs font-medium text-gray-700 dark:text-gray-300">
          {value}
        </p>
      </div>
    </div>
  );
}

function FlagPill({
  label,
  color,
}: {
  label: string;
  color: 'red' | 'yellow' | 'blue';
}) {
  const colors = {
    red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
    yellow:
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${colors[color]}`}
    >
      {label}
    </span>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
